import { Controller } from "@hotwired/stimulus"

/**
 * Reports Hub page-level controller.
 *
 * Mounted on the `.reports-page` root element (`app/views/exhibitor/reports/
 * index.html.erb`). This is the natural scope for behaviors that span the
 * filter bar + tab bar + tab content frame but should NOT leak into other
 * areas of the app.
 *
 * --- Phase 1 ---
 * Declared as a stub so `data-controller="reports-page"` resolves cleanly
 * and exposes event timezone + event_exhibitor id as values for child
 * controllers that need page-level context.
 *
 * --- Phase 8 — Chartkick + Turbo Frame init bridge ---
 * Chartkick's helper (`area_chart`, `column_chart`, `bar_chart`, etc.)
 * emits an inline initializer alongside each chart container:
 *
 *   <div id="la-leads-per-day">Loading...</div>
 *   <script>
 *     (function () {
 *       if (document.documentElement.hasAttribute("data-turbo-preview")) return;
 *       var createChart = function () {
 *         new Chartkick["AreaChart"]("la-leads-per-day", {...}, {...});
 *       };
 *       if ("Chartkick" in window) createChart();
 *       else window.addEventListener("chartkick:load", createChart, true);
 *     })();
 *   </script>
 *
 * On a full page load this script runs via the browser's native HTML parser.
 * On a Turbo Frame swap, however, the browser does NOT execute inline
 * scripts inside the swapped HTML — Turbo Frames intentionally diverge from
 * Turbo Drive on this point. So Chartkick's initializer never runs and the
 * chart container stays at "Loading..." (or a blank canvas) until a hard
 * refresh. The `turbo:frame-render` bridge below re-executes those scripts.
 *
 * Stimulus-managed charts (chart_controller, multi_heatmap_controller)
 * don't have this problem because Stimulus's MutationObserver re-runs
 * `connect()` for any controller that enters the DOM via a frame swap.
 * Chartkick charts are not Stimulus controllers — they're raw `<div>`s
 * with a sibling `<script>` — so they need this explicit bridge.
 *
 * --- Phase 8 follow-up — blank charts after tab-away-and-back ---
 *
 * Symptom: Chartkick charts render on cold load but go BLANK after clicking
 * to another tab and back.
 *
 * The Reports Hub tab links target the inner `reports_tab_content` Turbo
 * Frame AND carry `data-turbo-action="advance"`
 * (app/views/exhibitor/reports/_tab_bar.html.erb), so every tab click is a
 * frame swap that Turbo Drive *also* records in history. The observed
 * Turbo 7.3 event order for one such click is:
 *
 *   turbo:before-frame-render#reports_tab_content
 *   turbo:frame-render#reports_tab_content   ← (A) this bridge runs, charts built
 *   turbo:frame-load#reports_tab_content
 *   turbo:before-cache                       ← (B) dispose live charts for the snapshot
 *   turbo:before-render                       ← (C) Chartkick's GLOBAL destroyAll() wipes
 *                                                  the ENTIRE window.Chartkick.charts registry
 *                                                  (chartkick/dist/chartkick.js, gated on
 *                                                  Chartkick.config.autoDestroy !== false)
 *   turbo:render                              ← (D) Drive page render completes
 *   turbo:load
 *
 * So the frame bridge at (A) DID rebuild the charts — but Chartkick's Drive
 * `destroyAll()` at (C) runs immediately AFTER and tears them all down again,
 * with nothing rebuilding them. Net: blank. (The original diagnosis assumed
 * `turbo:frame-render` does not fire on the return path; in this Turbo
 * version it DOES, but it fires before the destroyAll, so its work is undone.)
 *
 * Note also that `.reports-page` persists across tab navigations — only the
 * inner frame content swaps — so the controller's `connect()` runs exactly
 * once and CANNOT be the rebuild hook for return navigations.
 *
 * Fix: reconcile the charts on a render path that runs AFTER Chartkick's
 * destroyAll — i.e. on `turbo:render` (event D), the Drive page-render event
 * that fires once per Drive navigation, after (C). That rebuilds whatever the
 * destroyAll wiped. We keep the `turbo:frame-render` bridge for the COLD-LOAD
 * path: on first paint the lazy frame is fetched (frame-render fires) with NO
 * following Drive render, so the bridge is the only thing that builds the
 * charts there.
 *
 * We deliberately do NOT flip `Chartkick.config.autoDestroy = false`: that
 * flag is global (Chartkick registers its Turbo handler exactly once, behind
 * a `!window.Chartkick` guard at import time), and disabling it would stop the
 * auto-cleanup that the 13+ Chartkick charts elsewhere in the app (admin
 * dashboards, exhibitor dashboard / profile / live dashboard, etc.) rely on.
 * Keeping Chartkick's global behavior intact, this controller becomes the
 * single owner of the Reports Hub chart lifecycle:
 *
 *   * turbo:frame-render  → reconcile the swapped frame (cold-load fetch and
 *                           any genuine frame refetch — original path).
 *   * turbo:render        → reconcile the Reports Hub (every Drive tab nav /
 *                           restoration / back-forward — rebuilds after the
 *                           global destroyAll that just ran).
 *   * turbo:before-cache  → dispose live Chartkick instances so the cached
 *     / disconnect()        snapshot never holds Chart.js objects bound to
 *                           soon-to-be-detached canvases (mirrors the tooltip
 *                           / DataTables dispose pattern in
 *                           packs/application.js).
 *
 * Every path routes through one idempotent reconcile() (destroy-stale →
 * re-execute inline init scripts), so a chart that already exists is torn
 * down and rebuilt rather than duplicated — no path can double-render or leak,
 * even though tab clicks fire BOTH frame-render and turbo:render.
 *
 * The `turbo:render` and `turbo:before-cache` listeners are document-level
 * (those Drive events fire on `document`, not on the controller element), but
 * they are SCOPED to the Reports Hub by only ever touching chart ids found
 * inside `this.element`. When the controller is connected the reports page is
 * on screen, so unrelated Chartkick charts elsewhere are never disturbed; and
 * `disconnect()` removes the listeners so they do nothing once the user leaves
 * the Reports Hub entirely.
 *
 * Eligibility rules for inline-script re-execution:
 *   - Skip `<script src="...">` — external scripts have their own load
 *     semantics and Turbo Frames handle them through `data-turbo-eval`.
 *   - Skip `<script type="module">` — modules are one-shot by spec; re-
 *     executing them is undefined.
 *   - Skip non-JS `<script>` types (e.g. `application/json` data blocks,
 *     `application/ld+json` schema.org markup).
 *   - Skip scripts marked `data-turbo-eval="false"` (the framework idiom).
 *
 * --- LRPO-5178 — sticky tab-bar offset tracks the ACTUAL strip height ---
 *
 * The Reports Hub stacks two sticky elements: the `.reports-context-strip`
 * header (top:0, z-index:1020) and, below it, the `.report-tabs` bar
 * (top: var(--reports-strip-h, 3.5rem), z-index:1019). The tab bar offsets
 * itself under the strip via the `--reports-strip-h` CSS custom property,
 * declared once on `.reports-page` as a STATIC `3.5rem` (56px) literal.
 *
 * But the strip's height is VARIABLE — it is a `flex-wrap` header, so it grows
 * to multiple rows on narrow viewports, with long event names, or when the
 * "Custom" date preset reveals the extra date-range row. When the real strip
 * exceeds 56px, the tab bar still pins at top:56px — INSIDE the taller strip —
 * and the strip overlaps/clips the first row of tabs on scroll.
 *
 * Fix: measure the live strip with a ResizeObserver and write its
 * `offsetHeight` (border-box, so it already includes the strip's 1px border)
 * to `--reports-strip-h` on `this.element` (the `.reports-page` root, where the
 * var is declared and which `.report-tabs` descends from). That overrides the
 * SCSS literal so the tab bar sits flush just below the strip — no overlap, no
 * gap — at whatever height the strip currently is. The SCSS `3.5rem` stays as
 * the first-paint fallback before JS boots.
 *
 * One ResizeObserver on the persistent strip element covers ALL height-change
 * causes (viewport resize, flex-wrap, Custom-preset row reveal, web-font load),
 * so we do NOT also hook the filter `change` events. The strip element itself
 * persists across tab navigations — only the inner `reports_tab_content` frame
 * swaps, NOT the header — so the single observer (and the controller's single
 * `connect()`) keeps working for the life of the page. We also write the value
 * once synchronously on connect rather than relying solely on the first async
 * RO callback, so the offset is correct on first paint right after JS boots.
 */
export default class extends Controller {
  static targets = ["strip"]

  static values = {
    timezone:          { type: String, default: "UTC" },
    eventExhibitorId:  { type: Number, default: 0 }
  }

  connect() {
    this._onFrameRender = this._handleFrameRender.bind(this)
    this._onTurboRender = this._handleTurboRender.bind(this)
    this._onBeforeCache = this._handleBeforeCache.bind(this)
    this._healingTo = null // LRPO-5365 — stale-tab self-heal latch

    // Frame swaps (cold-load fetch + any genuine refetch). `turbo:frame-render`
    // bubbles, so any descendant frame swap reaches this element-scoped
    // listener; frames outside Reports Hub never touch it.
    this.element.addEventListener("turbo:frame-render", this._onFrameRender)

    // Drive page render — fires AFTER Chartkick's global destroyAll on every
    // Drive navigation (tab click / restoration / back-forward). Document-
    // level because Drive events fire on document; scoped by only touching
    // chart ids inside `this.element`.
    document.addEventListener("turbo:render", this._onTurboRender)

    // Dispose live charts just before Turbo snapshots the page for its cache,
    // so the snapshot is inert (no Chart.js objects pointing at canvases that
    // will be detached). Mirrors the tooltip / DataTables pattern.
    document.addEventListener("turbo:before-cache", this._onBeforeCache)

    // Cold load: `.reports-page` enters the DOM while the tab frame is still
    // the loading skeleton — reconcile finds no chart scripts and is a no-op.
    // The subsequent lazy frame fetch fires `turbo:frame-render`, which builds
    // the charts exactly once. (Kept idempotent so this is harmless if the
    // frame ever arrives before connect.)
    this._reconcile(this.element)

    // LRPO-5480 — sync the context-strip section label to the current frame
    // content on first paint (cold load). Idempotent with the SSR value.
    this._syncContextMeta(this.element.querySelector("#reports_tab_content"))

    // LRPO-5533 — same for the date filter's visibility. Idempotent with the
    // SSR value (each non-filterable view stamps the modifier server-side), so
    // this only ever matters on a restored Turbo snapshot.
    this._syncDateFilterVisibility(this.element.querySelector("#reports_tab_content"))

    // LRPO-5178 — keep the sticky tab-bar offset (`--reports-strip-h`) in sync
    // with the live strip height (see class comment). Bound handler stored on
    // the instance so `disconnect()` can tear the observer down cleanly,
    // mirroring the `_onFrameRender` pattern above.
    this._onStripResize = this._handleStripResize.bind(this)
    if (this.hasStripTarget && typeof ResizeObserver === "function") {
      this._stripResizeObserver = new ResizeObserver(this._onStripResize)
      this._stripResizeObserver.observe(this.stripTarget)
      // Write the measured height once synchronously so the offset is correct
      // on the first paint after JS boots — don't wait for the first async RO
      // callback. No-op (guarded) if the strip is somehow absent.
      this._writeStripHeight()
    }
    // LRPO-5955 — Compare's sticky sub-nav parks at strip + tab-bar height.
    // Measure the tab bar the same way as the strip so a too-tall 3.5rem
    // fallback cannot open a gap content scrolls through.
    this._writeTabsHeight()
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-render", this._onFrameRender)
    document.removeEventListener("turbo:render", this._onTurboRender)
    document.removeEventListener("turbo:before-cache", this._onBeforeCache)
    // Leaving the Reports Hub entirely — tear down our charts so we never
    // leak Chart.js instances.
    this._destroyChartkickChartsWithin(this.element)
    this._onFrameRender = null
    this._onTurboRender = null
    this._onBeforeCache = null

    // LRPO-5178 — stop measuring the strip and drop the handle so the observer
    // can't fire (or leak) once the Reports Hub is gone.
    if (this._stripResizeObserver) {
      this._stripResizeObserver.disconnect()
      this._stripResizeObserver = null
    }
    this._onStripResize = null
  }

  _handleFrameRender(event) {
    const frame = event.target
    if (!frame) return
    this._reconcile(frame)
    this._syncContextMeta(frame) // LRPO-5480
    this._syncDateFilterVisibility(frame) // LRPO-5533
    this._healStaleTab(frame) // LRPO-5365
  }

  // LRPO-5533 — hide the date filter on the tabs that do not consume it.
  //
  // The Reports Hub date filter (Full Event / Today / Yesterday / Custom) is
  // INERT on two tabs, by design:
  //
  //   * "Reports" (report_templates) — each ReportTemplate carries its own
  //     `date_range`; the tab-bar params are never read. Its tab link is even
  //     marked `data-reports-tabs-filter-skip` (LRPO-5247) so the params are
  //     never threaded onto the href in the first place.
  //   * "ROI Calculator" (roi_calculators#show + roi_scenarios#compare) — the
  //     ROI surface owns its horizon via ExhibitorEventCost#time_horizon_months.
  //
  // Neither controller reads params[:date_preset] / :date_from / :date_to (both
  // hard-code `@date_preset = 'full_event'` purely so the segmented control has
  // something to render checked), and no view on either surface touches a date
  // ivar. So the control did nothing at all there — and submitting it snapped
  // straight back to "Full Event", because the server re-hard-codes the preset.
  //
  // Server-side each non-filterable view stamps
  // `reports-context-strip--no-date-filter` on its own strip, which is enough
  // for a full-page load (and for a JS-off client). But the strip lives OUTSIDE
  // `reports_tab_content` and PERSISTS across tab navigations — only the frame's
  // children swap — so on the frame-swap path the strip on screen is whichever
  // one the last FULL-PAGE load baked. Reconcile it from the content that just
  // landed: the non-filterable tab bodies carry a `[data-reports-no-date-filter]`
  // marker inside the frame; absence of the marker means a filterable tab, and
  // the filter must come BACK. Exactly the _syncContextMeta (LRPO-5480)
  // mechanism, and deliberately keyed off the rendered CONTENT rather than the
  // URL / active link so an out-of-order response (see _healStaleTab) can never
  // leave the chrome disagreeing with the view on screen.
  //
  // Scoped to the `reports_tab_content` frame so unrelated frame renders (the
  // geography city drilldown, the report-render status poller) never touch it.
  _syncDateFilterVisibility(frame) {
    if (!frame || frame.id !== "reports_tab_content") return
    if (!this.hasStripTarget) return

    const hideFilter = !!frame.querySelector("[data-reports-no-date-filter]")
    this.stripTarget.classList.toggle("reports-context-strip--no-date-filter", hideFilter)

    // Showing/hiding the filter changes the strip's height, and the sticky tab
    // bar offsets itself under the strip via `--reports-strip-h` (LRPO-5178).
    // Re-measure synchronously rather than waiting on the ResizeObserver, which
    // lands a frame late.
    this._writeStripHeight()
  }

  // LRPO-5480 — keep the context-strip section label in sync with the frame.
  //
  // The `.reports-context-strip__meta` label ("Reports" on the Hub tabs, "ROI"
  // on the ROI Calculator / Compare surfaces) lives in the `.reports-context-
  // strip` header, which sits OUTSIDE `reports_tab_content` and persists across
  // tab navigations — only the inner frame swaps. So the label was frozen at
  // whatever the last FULL-PAGE load rendered: open ROI Calculator → "Compare
  // scenarios" is a `_top` (full-page) nav that bakes "ROI" into the strip, and
  // every subsequent frame-only tab click then left "ROI" stuck there until a
  // hard refresh (LRPO-5480).
  //
  // Fix: each frame's content declares its own section label via
  // `[data-reports-context-meta]` (roi_calculators/show + roi_scenarios/compare
  // carry "ROI"; the report tabs carry nothing = the "Reports" default). On
  // every frame render we re-derive the label from the freshly-swapped content
  // and write it into the persistent strip, so the label always reflects the
  // section on screen — in BOTH directions (→ ROI and back to a report tab).
  // Scoped to the `reports_tab_content` frame so unrelated frame renders (e.g.
  // the geography city drilldown) never touch the label.
  _syncContextMeta(frame) {
    if (!frame || frame.id !== "reports_tab_content") return

    const metaEl = this.element.querySelector(".reports-context-strip__meta")
    if (!metaEl) return

    const marker = frame.querySelector("[data-reports-context-meta]")
    const label = (marker && marker.dataset.reportsContextMeta) || "Reports"

    if (metaEl.textContent !== label) metaEl.textContent = label
  }

  // LRPO-5365 — self-heal an out-of-order tab response.
  //
  // Tab links flip the active indicator SYNCHRONOUSLY on click
  // (reports-tabs#setActive) but the frame body arrives ASYNCHRONOUSLY. If a
  // user clicks tab A then quickly tab B and A's response lands AFTER B's, the
  // frame ends up showing A's content while B is highlighted — the "previous
  // tab's view" QA reported. Each rendered tab section carries data-tab="<key>"
  // and each tab link carries data-report-tab-key="<key>", so on every frame
  // render we compare the ACTIVE tab's key to the tab that actually rendered;
  // on a mismatch we re-fetch the active tab (frame.reload() re-requests the
  // frame's current src, which Turbo already set to the active tab on the last
  // click). Guarded by _healingTo so a re-fetch in flight isn't retriggered,
  // and it converges if the user keeps clicking (each render re-checks).
  _healStaleTab(frame) {
    if (!frame || frame.id !== "reports_tab_content") return

    const renderedSection = frame.querySelector("[data-tab]")
    const renderedKey = renderedSection && renderedSection.dataset.tab
    // Some tabs (Templates / ROI) render no data-tab section — can't compare,
    // so never heal (avoids a false-positive re-fetch loop on those tabs).
    if (!renderedKey) return

    const activeLink = this.element.querySelector('[data-reports-tabs-target="link"].active')
    const activeKey = activeLink && activeLink.dataset.reportTabKey
    if (!activeKey) return

    if (activeKey === renderedKey) {
      this._healingTo = null // in sync — the common case; clear any heal latch
      return
    }

    // A stale (out-of-order) response landed. Re-fetch the active tab unless a
    // heal to that same tab is already in flight.
    if (this._healingTo === activeKey) return
    this._healingTo = activeKey
    if (typeof frame.reload === "function") {
      frame.reload()
    } else if (activeLink.href) {
      frame.setAttribute("src", activeLink.href)
    }
  }

  _handleTurboRender() {
    // Rebuild whatever Chartkick's destroyAll just wiped. Scoped to the
    // Reports Hub root; no-op if the reports page has no Chartkick charts in
    // the DOM at this moment.
    this._reconcile(this.element)

    // LRPO-5247 — resync the shell chrome (tab hrefs + filter bar) to the
    // current URL. `turbo:render` fires AFTER the Drive URL advance (event D
    // in the class comment sequence), so `window.location.search` is already
    // the advanced value here — correct timing for both the filter-apply path
    // and the tab-click path (both produce Drive navigations via
    // `data-turbo-action="advance"` and both fire `turbo:render` at (D)).
    //
    // Ordered BEFORE _writeStripHeight() below: _syncChrome() can toggle the
    // custom-range row's `d-none` (which changes the strip's height), so the
    // strip must be re-measured AFTER this sync — never before.
    this._syncChrome()

    // LRPO-5178 — re-measure the strip on every Drive render. Reports Hub tab
    // links carry `data-turbo-action="advance"`, so each tab click is a Drive
    // navigation. The new frame can reflow the strip to a DIFFERENT height
    // (filter-bar wrap, Custom row, longer title). The ResizeObserver does
    // NOT reliably re-fire here — if the strip's offsetHeight is numerically
    // unchanged from the last write the RO callback is skipped, and even
    // when it does fire it lands a frame late. `turbo:render` fires once per
    // Drive nav, after the frame is in the DOM, so a synchronous re-write
    // here restores a correct offset immediately. Guarded no-op when the
    // strip is absent. (LRPO-5955: before-cache no longer drops the vars;
    // re-measure still belongs here because the strip can change height.)
    this._writeStripHeight()
  }

  _handleBeforeCache() {
    // Only dispose charts that live inside the Reports Hub root so unrelated
    // Chartkick charts elsewhere keep their own lifecycle.
    this._destroyChartkickChartsWithin(this.element)
    // LRPO-5955 — do not drop the measured sticky offsets here: the outgoing
    // page is still visible during Turbo.visit, and falling back to 3.5rem
    // opens a gap. connect() / _handleTurboRender() still re-measure.
  }

  // LRPO-5247 — Resync the shell chrome to the current browser URL so the
  // baked-at-SSR tab hrefs and the filter-bar preset state stay honest after
  // any Drive navigation (filter apply, tab switch, back/forward).
  //
  // Called from _handleTurboRender(), which runs on `turbo:render` — the Drive
  // page-render event that fires AFTER the URL has already been advanced (event
  // D in the class-comment sequence). Both the filter-apply path (form with
  // data-turbo-action="advance") and the tab-click path (link with
  // data-turbo-action="advance") produce Drive navigations that fire
  // `turbo:render`, so this single hook covers every case where the URL can
  // advance while the shell chrome stays mounted.
  //
  // Three operations:
  //
  //   1. Rewrite each filterable tab link's query string to mirror the current
  //      URL's query string (the SAME set the server propagates), preserving the
  //      link's path. Links carrying `data-reports-tabs-filter-skip` (Templates /
  //      ROI Calculator) are skipped — those tabs intentionally own their own
  //      date horizons and must stay param-free.
  //
  //   2. Sync the filter bar UI from the URL:
  //      — Check the radio whose value matches `date_preset` (default
  //        `full_event`). Does NOT submit the form; purely a visual sync.
  //      — Populate `date_from` / `date_to` inputs from the URL.
  //      — Toggle the custom-range container's `d-none` class based on
  //        whether the active preset is `custom`.
  //      — Toggle the "Clear filters" wrapper's `d-none` on whether the URL
  //        carries a non-default filter (LRPO-5512).
  //
  //   3. Re-point the filter <form> action and the "Clear filters" link href at
  //      the current tab path (LRPO-5145) so applying/clearing the filter on a
  //      non-Overview tab targets THAT tab, not the SSR-frozen overview path.
  //
  // All three operations are scoped to `this.element` (the `.reports-page` root)
  // so nothing outside the Reports Hub is touched. Idempotent: running twice
  // with the same URL produces the same result with no side effects.
  _syncChrome() {
    const params = new URLSearchParams(window.location.search)
    const preset = params.get('date_preset') || 'full_event'

    // --- 1. Tab link href resync -------------------------------------------
    const links = this.element.querySelectorAll('[data-reports-tabs-target="link"]')
    links.forEach(link => {
      // Skip tabs that intentionally ignore the date-filter params.
      if (link.dataset.reportsTabsFilterSkip) return

      // Wholesale copy of the WHOLE query string is intentional — NOT a
      // date-only allowlist. The server builds these hrefs with
      // `report_tab_items(request.query_parameters)` (reports_helper.rb), which
      // threads ALL current query params onto every tab href at SSR. The Reports
      // Hub legitimately carries non-date params (state, section, view, rep_id,
      // event_exhibitor_ids[], heatmap start_time/end_time, …), and threading
      // them across report surfaces is deliberate (e.g. LRPO-5208 threads the
      // date window into drilldowns). Mirroring `window.location.search` verbatim
      // keeps the client-rewritten hrefs byte-identical to the server-rendered
      // ones; an allowlist would diverge from the server and drop legit params.
      const u = new URL(link.href)
      u.search = window.location.search
      link.href = u.toString()
    })

    // --- 2. Filter bar UI resync -------------------------------------------
    // Preset radios — check the one matching the URL, uncheck the rest.
    const presetInputs = this.element.querySelectorAll('input[name="date_preset"]')
    presetInputs.forEach(input => {
      input.checked = input.value === preset
    })

    // Custom-range container visibility. This intentionally parallels
    // `reports-filter#_syncCustomVisibility` (same `d-none` toggle on the same
    // customRange target / same `preset === 'custom'` condition) — do NOT
    // "deduplicate" the two: they run on DIFFERENT triggers (reports-filter on
    // its own Stimulus connect + user `change`; this on `turbo:render`), so each
    // controller must own the toggle for the paths it covers.
    const customRange = this.element.querySelector('[data-reports-filter-target="customRange"]')
    if (customRange) {
      customRange.classList.toggle('d-none', preset !== 'custom')
    }

    // Date inputs — populate from URL params, scoped to the visible custom-range
    // container only. These visible inputs (date_field_tag :date_from /
    // :date_to) are the SINGLE source of those params: `_filter_bar.html.erb`
    // no longer renders any duplicate hidden date_from/date_to fields outside
    // customRange (removed in LRPO-5145 — the trailing blanks won Rack's
    // last-wins race and broke the Custom-apply path). So this client-side
    // refresh keeps the only date inputs in sync with the URL for back/forward +
    // deep-link honesty; there is nothing else to fight with.
    //
    // LRPO-5513 — UNLESS the user has unapplied edits. reports-filter stamps
    // `data-reports-filter-dirty="true"` on the filter bar whenever the typed
    // range differs from the one last submitted. The Drive render we are
    // handling belongs to the range they submitted BEFORE that edit — on a slow
    // query it can easily land while they are mid-correction — and overwriting
    // their input with the abandoned range re-arms the exact trap LRPO-5513
    // removes (they change the range, and the control changes it back under
    // them). The URL still wins for every clean render; unapplied user input is
    // simply never destroyed.
    const hasUnappliedEdits = !!this.element.querySelector('[data-reports-filter-dirty="true"]')
    if (customRange && !hasUnappliedEdits) {
      const fromInput = customRange.querySelector('input[name="date_from"]')
      const toInput   = customRange.querySelector('input[name="date_to"]')
      if (fromInput) fromInput.value = params.get('date_from') || ''
      if (toInput)   toInput.value   = params.get('date_to')   || ''
    }

    // "Clear filters" visibility (LRPO-5512). The wrapper is ALWAYS in the DOM
    // (`_filter_bar.html.erb` only `d-none`-toggles it), because this shell is
    // never re-rendered by a frame swap — see the reports_filter_controller class
    // comment. reports-filter owns the toggle for direct user interaction
    // (preset / date change / Clear click); this owns it for URL-driven renders
    // (back/forward, deep link, the Drive render after an Apply or a Clear), and
    // both apply the SAME predicate: non-default preset OR either bound present.
    // Deliberately parallel, NOT deduplicated — same reasoning as the customRange
    // toggle above.
    const clearWrap = this.element.querySelector('[data-reports-filter-target="clear"]')
    if (clearWrap) {
      const filtersApplied =
        preset !== 'full_event' ||
        !!params.get('date_from') ||
        !!params.get('date_to')
      clearWrap.classList.toggle('d-none', !filtersApplied)
    }

    // --- 3. Filter form action + Clear-filters href resync (LRPO-5145) ------
    // The filter <form> action (form_with url: request.path) and the "Clear
    // filters" link href (link_to request.path) are baked at full-page SSR =
    // the FIRST tab loaded (overview). Tab nav is a Turbo-frame swap
    // (data-turbo-action="advance") that only swaps the frame + advances the
    // URL; this shell/form is never re-rendered, so both keep targeting
    // /exhibitor/reports/overview. Applying the date filter (or clearing it) on
    // any OTHER tab would then hit overview and show the (empty) Overview
    // content instead of filtering the active tab. Re-point both at the current
    // path (the active tab). The form is GET and serializes its own params, so
    // we set the bare pathname — no query string. Scoped to `this.element`.
    const path = window.location.pathname
    const form = this.element.querySelector('[data-reports-filter-target="form"]')
    if (form) form.action = path

    const clearLink = this.element.querySelector('.report-filter-bar__clear a')
    if (clearLink) clearLink.href = path
  }

  // LRPO-5178 — ResizeObserver callback: any strip size change re-measures and
  // re-writes the offset var. The RO fires on viewport resize, flex-wrap row
  // changes, the Custom-preset date-range row reveal, and font load alike.
  _handleStripResize() {
    this._writeStripHeight()
  }

  // LRPO-5178 — write the live strip's height (px) to `--reports-strip-h` on
  // the `.reports-page` root so the sticky `.report-tabs` bar sits flush just
  // below the strip. `offsetHeight` is the border-box height, so it already
  // includes the strip's 1px border — no buffer needed. Guarded so it's a
  // no-op if the strip target is somehow absent.
  _writeStripHeight() {
    if (!this.hasStripTarget) return
    const h = this.stripTarget.offsetHeight
    if (h > 0) {
      this.element.style.setProperty("--reports-strip-h", `${h}px`)
    }
    this._writeTabsHeight()
  }

  _writeTabsHeight() {
    const tabs = this.element.querySelector(".report-tabs")
    if (!tabs) return
    const h = tabs.offsetHeight
    if (h > 0) {
      this.element.style.setProperty("--report-tabs-h", `${h}px`)
    }
    // h === 0 (strip not yet laid out / hidden): leave the var UNSET so the
    // SCSS `3.5rem` fallback applies until a real measurement arrives via the
    // RO. A `var(--x, 3.5rem)` fallback only kicks in when the property is
    // UNSET — writing `0px` would OVERRIDE it and re-pin the tab bar at top:0.
  }

  // Single, idempotent rebuild path shared by every trigger. Destroying stale
  // registry entries before re-executing the inline init scripts means a chart
  // that already exists is torn down and rebuilt rather than duplicated — so
  // no path can double-render or leak, regardless of how many times it runs.
  _reconcile(root) {
    if (!root) return
    this._destroyChartkickChartsWithin(root)
    this._reexecuteInlineScripts(root)
  }

  // Chartkick keeps a registry at `window.Chartkick.charts` keyed by the
  // element id. Destroy every registry entry whose id appears inside `root`
  // before re-execution so the new IIFE replaces the slot cleanly and we don't
  // leak Chart.js charts.
  _destroyChartkickChartsWithin(root) {
    const Chartkick = window.Chartkick
    if (!Chartkick || !Chartkick.charts || !root) return

    Object.keys(Chartkick.charts).forEach(chartId => {
      const node = root.querySelector(`#${CSS.escape(chartId)}`)
      if (!node) return

      try {
        Chartkick.charts[chartId].destroy()
      } catch (_e) {
        // Chart.js sometimes throws if the underlying canvas was already
        // detached. Swallow — we're about to replace it anyway.
      }
      delete Chartkick.charts[chartId]
    })
  }

  _reexecuteInlineScripts(root) {
    const scripts = root.querySelectorAll("script:not([src])")
    scripts.forEach(oldScript => {
      if (!this._isExecutableScript(oldScript)) return

      const newScript = document.createElement("script")
      newScript.text = oldScript.text
      if (oldScript.id) newScript.id = oldScript.id
      // Preserve nonce so CSP allow-listing still applies on re-execution.
      if (oldScript.nonce) newScript.nonce = oldScript.nonce
      oldScript.replaceWith(newScript)
    })
  }

  _isExecutableScript(script) {
    // Honor the framework opt-out idiom.
    if (script.dataset && script.dataset.turboEval === "false") return false

    const type = (script.type || "").trim().toLowerCase()
    // Empty / classic JS types only. ES modules are deliberately skipped
    // because module scripts are one-shot per spec and re-running them is
    // undefined — none of the Reports Hub partials emit modules today.
    if (type === "" || type === "text/javascript" || type === "application/javascript") {
      return true
    }
    return false
  }
}
